import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type {
  ReportMessageListItem,
  ReportMessageListResponse,
  ReportMessagePageSize,
} from "@multi-chat/shared";
import { REPORT_MESSAGE_PAGE_SIZES } from "@multi-chat/shared";
import { ReportResultState } from "./ReportResultState.js";
import type { ReportEmptyKind } from "./report-empty-kind.js";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../components/channel/channel-presentation.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  incomingMessagePreview,
  messageTypeChipClass,
  messageTypeIcon,
  messageTypeLabelKey,
} from "./incoming-report-presentation.js";
import type { IncomingReportColumnId } from "./report-column-defs.js";
import { reportColumnHeaderDragHandlers } from "./report-column-header-drag.js";
import { ReportColumnResizeHandle } from "./ReportColumnResizeHandle.js";
import { ReportPaginationBar } from "./ReportPaginationBar.js";
import {
  contactsTableHeaderLabelClass,
  reportColumnStyle,
  reportTableBodyCellClass,
  reportTableBodyRowClass,
  reportTableClass,
  reportTableHeaderCellClass,
  reportTableHeadRowClass,
  reportTableScrollClass,
  reportTableWorkspaceClass,
} from "./report-table-presentation.js";
import {
  formatReportDateTime,
  recipientDisplayPrimary,
  recipientDisplaySecondary,
  showingRangeBounds,
} from "./outgoing-report-presentation.js";

function incomingColumnLabel(
  t: (key: string) => string,
  id: IncomingReportColumnId,
): string {
  switch (id) {
    case "platform":
      return t("reports.incoming.columns.channel");
    case "account":
      return t("reports.incoming.columns.endpoint");
    case "date":
    case "sender":
    case "message":
    case "type":
      return t(`reports.incoming.columns.${id}`);
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

export function IncomingReportTable({
  list,
  loading,
  page,
  pageSize,
  emptyKind,
  visibleColumns,
  widths,
  onResizeColumn,
  onReorderColumn,
  onPageChange,
  onPageSizeChange,
  onResetFilters,
  onOpenMessage,
}: {
  list: ReportMessageListResponse | null;
  loading: boolean;
  page: number;
  pageSize: number;
  emptyKind: ReportEmptyKind;
  visibleColumns: readonly IncomingReportColumnId[];
  widths: Readonly<Record<IncomingReportColumnId, number>>;
  onResizeColumn: (id: IncomingReportColumnId, width: number) => void;
  onReorderColumn: (fromId: IncomingReportColumnId, toId: IncomingReportColumnId) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: ReportMessagePageSize) => void;
  onResetFilters: () => void;
  onOpenMessage?: (messageId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const items = list?.items ?? [];
  const total = list?.totalCount ?? 0;
  const totalPages = Math.max(list?.totalPages ?? 1, 1);
  const range = showingRangeBounds({ page, pageSize, count: items.length });
  const colCount = Math.max(visibleColumns.length, 1);
  const dragFromRef = useRef<IncomingReportColumnId | null>(null);
  const resizeRef = useRef<{
    id: IncomingReportColumnId;
    startX: number;
    startWidth: number;
    rtl: boolean;
  } | null>(null);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const active = resizeRef.current;
      if (active == null) return;
      const delta = active.rtl ? active.startX - event.clientX : event.clientX - active.startX;
      onResizeColumn(active.id, active.startWidth + delta);
    };
    const onUp = () => {
      resizeRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [onResizeColumn]);

  return (
    <div className={reportTableWorkspaceClass} data-testid="reports-incoming-table">
      <div className={reportTableScrollClass}>
        <table className={reportTableClass} style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
          <thead>
            <tr className={reportTableHeadRowClass}>
              {visibleColumns.map((columnId) => {
                const label = incomingColumnLabel(t, columnId);
                return (
                  <th
                    key={columnId}
                    className={reportTableHeaderCellClass}
                    style={reportColumnStyle(widths[columnId])}
                    {...reportColumnHeaderDragHandlers(columnId, dragFromRef, onReorderColumn)}
                    data-testid={`reports-incoming-column-${columnId}`}
                  >
                    <span className={contactsTableHeaderLabelClass}>{label}</span>
                    <ReportColumnResizeHandle
                      label={t("reports.chrome.resizeColumn", { column: label })}
                      onResizeStart={(clientX) => {
                        const rtl =
                          typeof document !== "undefined" &&
                          getComputedStyle(document.documentElement).direction === "rtl";
                        resizeRef.current = {
                          id: columnId,
                          startX: clientX,
                          startWidth: widths[columnId],
                          rtl,
                        };
                      }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0
              ? Array.from({ length: 6 }, (_, index) => (
                  <tr key={`sk-${index}`} className={reportTableBodyRowClass}>
                    {visibleColumns.map((columnId) => (
                      <td
                        key={columnId}
                        className={reportTableBodyCellClass}
                        style={reportColumnStyle(widths[columnId])}
                      >
                        <div className="h-4 w-24 animate-pulse rounded bg-surface-muted motion-reduce:animate-none" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}

            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={cn(reportTableBodyCellClass, "px-4 py-10 text-center")}>
                  <ReportResultState
                    kind={emptyKind}
                    i18nPrefix="reports.incoming"
                    onResetFilters={onResetFilters}
                    testId="reports-incoming-empty"
                  />
                </td>
              </tr>
            ) : null}

            {items.map((row) => (
              <IncomingRow
                key={row.messageId}
                row={row}
                visibleColumns={visibleColumns}
                widths={widths}
                onOpenMessage={onOpenMessage}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ReportPaginationBar
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={totalPages}
        pageSizes={REPORT_MESSAGE_PAGE_SIZES}
        onPageChange={onPageChange}
        onPageSizeChange={(size) => onPageSizeChange(size as ReportMessagePageSize)}
        showingLabel={t("reports.incoming.showingRange")}
        pageOfLabel={t("reports.incoming.pageOf")}
        prevLabel={t("reports.incoming.prev")}
        nextLabel={t("reports.incoming.next")}
        pageSizeLabel={t("reports.incoming.pageSize")}
        testIdPrefix="reports-incoming"
        rangeStart={range.start}
        rangeEnd={range.end}
      />
    </div>
  );
}

function IncomingRow({
  row,
  visibleColumns,
  widths,
  onOpenMessage,
}: {
  row: ReportMessageListItem;
  visibleColumns: readonly IncomingReportColumnId[];
  widths: Readonly<Record<IncomingReportColumnId, number>>;
  onOpenMessage?: (messageId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const secondary = recipientDisplaySecondary(row.contact);
  const endpointSecondary =
    row.endpoint.externalEndpointId !== row.endpoint.displayLabel
      ? row.endpoint.externalEndpointId
      : null;
  const TypeIcon = messageTypeIcon(row.messageType);
  const interactive = onOpenMessage != null;

  const renderCell = (columnId: IncomingReportColumnId): React.ReactNode => {
    switch (columnId) {
      case "date":
        return (
          <LtrValue className="tabular-nums text-[0.8125rem]">
            {formatReportDateTime(row.createdAt, i18n.language)}
          </LtrValue>
        );
      case "sender":
        return (
          <div className="grid min-w-0 gap-0.5 overflow-hidden">
            <span className="truncate font-medium text-foreground [unicode-bidi:plaintext]" dir="auto">
              {recipientDisplayPrimary(row.contact)}
            </span>
            {secondary ? (
              <LtrValue className="truncate text-[0.6875rem] text-muted">{secondary}</LtrValue>
            ) : null}
          </div>
        );
      case "platform":
        return (
          <ChannelIdentityBadge
            platformType={row.platform}
            size="table"
            label={t(`channel.${channelLabelI18nKey(row.platform)}`)}
          />
        );
      case "account":
        return (
          <div className="grid min-w-0 gap-0.5">
            <span className="truncate text-[0.8125rem] text-foreground">
              {row.endpoint.displayLabel ?? row.endpoint.externalEndpointId}
            </span>
            {endpointSecondary ? (
              <LtrValue className="truncate text-[0.75rem] text-muted">
                {endpointSecondary}
              </LtrValue>
            ) : null}
          </div>
        );
      case "message":
        return (
          <span
            className="line-clamp-2 text-[0.8125rem] text-foreground [unicode-bidi:plaintext]"
            dir="auto"
          >
            {incomingMessagePreview(t, row)}
          </span>
        );
      case "type":
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium",
              messageTypeChipClass(row.messageType),
            )}
          >
            <TypeIcon size={12} aria-hidden="true" />
            {t(messageTypeLabelKey(row.messageType))}
          </span>
        );
      default: {
        const _exhaustive: never = columnId;
        return _exhaustive;
      }
    }
  };

  return (
    <tr
      className={reportTableBodyRowClass}
      data-clickable={interactive ? "true" : undefined}
      data-message-id={row.messageId}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onOpenMessage(row.messageId) : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenMessage(row.messageId);
              }
            }
          : undefined
      }
    >
      {visibleColumns.map((columnId) => (
        <td
          key={columnId}
          className={reportTableBodyCellClass}
          style={reportColumnStyle(widths[columnId])}
        >
          {renderCell(columnId)}
        </td>
      ))}
    </tr>
  );
}
